import React, { useState, useEffect } from 'react';
import { useDragDrop } from '../hooks/useDragDrop';

const ArrangeScreen = ({ 
  cricketers = [], 
  statistic = '', 
  isHost = false, 
  currentRound = 1,
  totalRounds = 5,
  onSubmitArrangement 
}) => {
  const {
    items: arrangedCricketers,
    draggedItem,
    draggedOverIndex,
    isDragging,
    isTouchDevice,
    handleDragStart,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleDragOver,
    handleDragLeave,
    handleDragEnter,
    handleDrop,
    resetItems
  } = useDragDrop(cricketers);

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasUserReordered, setHasUserReordered] = useState(false);

  useEffect(() => {
    if (cricketers && cricketers.length > 0 && !isDragging && !hasUserReordered) {
      console.log('Setting up cricketers for arrange mode:', cricketers);
      resetItems(cricketers);
    }
  }, [cricketers, resetItems, isDragging, hasUserReordered]);

  // Reset user reorder flag when starting a new round
  useEffect(() => {
    setHasUserReordered(false);
    setHasSubmitted(false);
  }, [currentRound]);

  const handleSubmit = () => {
    if (hasSubmitted) return;
    
    const arrangement = arrangedCricketers.map(cricketer => cricketer.id);
    onSubmitArrangement(arrangement);
    setHasSubmitted(true);
  };


  const handleDropWithReorderFlag = (e, dropIndex) => {
    const reorderOccurred = handleDrop(e, dropIndex);
    // Set flag to indicate user has manually reordered items
    if (reorderOccurred) {
      console.log('User reordered items, setting hasUserReordered to true');
      setHasUserReordered(true);
    }
    return reorderOccurred;
  };

  const handleTouchEndWithReorderFlag = (e) => {
    const reorderOccurred = handleTouchEnd(e);
    // Set flag to indicate user has manually reordered items
    if (reorderOccurred) {
      console.log('User reordered items via touch, setting hasUserReordered to true');
      setHasUserReordered(true);
    }
    return reorderOccurred;
  };

  return (
    <div className="screen active">
      <div className="card arrange-card">
        <h2>Arrange Players</h2>
        <div className="round-info">
          <span className="round-badge">Round {currentRound} of {totalRounds}</span>
        </div>
        <p className="arrange-instruction">
          Drag and drop the cricketers to arrange them from <strong>best</strong> to <strong>worst</strong> in:
        </p>
        <h3 className="statistic-title">{statistic}</h3>
        
        <div className="arrange-container">
          <div className="ranking-labels">
            <span className="rank-label best">Best</span>
            <span className="rank-label worst">Worst</span>
          </div>
          
          <div 
            className="arrange-list"
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEndWithReorderFlag}
          >
            {arrangedCricketers.map((cricketer, index) => (
              <div
                key={`${cricketer.id}-${index}`}
                data-drop-index={index}
                className={`arrange-item ${
                  draggedItem?.index === index ? 'dragging' : ''
                } ${
                  draggedOverIndex === index ? 'drag-over' : ''
                }`}
                draggable={!isTouchDevice && !hasSubmitted}
                onDragStart={(e) => {
                  console.log('Drag start for:', cricketer.name);
                  if (!hasSubmitted) {
                    handleDragStart(e, cricketer, index);
                  }
                }}
                onDragOver={(e) => {
                  if (!hasSubmitted) {
                    handleDragOver(e, index);
                  }
                }}
                onDragEnter={(e) => {
                  if (!hasSubmitted) {
                    handleDragEnter(e);
                  }
                }}
                onDragLeave={(e) => {
                  handleDragLeave(e);
                }}
                onDrop={(e) => {
                  console.log('Drop event for index:', index);
                  if (!hasSubmitted) {
                    handleDropWithReorderFlag(e, index);
                  }
                }}
                onTouchStart={(e) => {
                  if (!hasSubmitted) {
                    handleTouchStart(e, cricketer, index);
                  }
                }}
                style={{
                  opacity: draggedItem?.index === index ? 0.5 : 1,
                  transform: draggedOverIndex === index ? 'scale(1.05)' : 'scale(1)',
                  cursor: hasSubmitted ? 'not-allowed' : (isTouchDevice ? 'grab' : 'move'),
                  transition: 'all 0.2s ease'
                }}
              >
                <div className="arrange-item-content">
                  <div className="position-number">{index + 1}</div>
                  <div className="cricketer-info">
                    <h4 className="cricketer-name">{cricketer.name}</h4>
                  </div>
                  <div className="drag-handle">
                    {isTouchDevice ? '⋮⋮' : '↕️'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {isDragging && isTouchDevice && (
          <div className="touch-instruction">
            Release to drop here
          </div>
        )}

        <div className="arrange-actions">
          {!hasSubmitted ? (
            <button 
              className="submit-btn primary-btn"
              onClick={handleSubmit}
              disabled={arrangedCricketers.length === 0}
            >
              Submit Arrangement
            </button>
          ) : (
            <div className="submitted-message">
              ✅ Arrangement submitted! Waiting for other players...
            </div>
          )}
        </div>

        {isTouchDevice && !hasSubmitted && (
          <div className="mobile-instructions">
            <p>📱 <strong>Mobile Instructions:</strong></p>
            <ul>
              <li>Tap and hold a player to start dragging</li>
              <li>Move your finger to the desired position</li>
              <li>Release to drop</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArrangeScreen;